<template>
    <div id="article" v-editable="article">
        <ArticleHero :data="heroData" />
        <div class="container">
            <ArticleView :article="articleData" />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ArticleContentSectionType } from '#shared/types/ArticleContentSectionType';
import type { ArticleHeroType } from '#shared/types/ArticleHeroType';
import type { ArticleType } from '#shared/types/ArticleType';
import type { ImageType } from '#shared/types/ImageType';
import type { LinkType } from '#shared/types/LinkType';
import type { TestimonialCardType } from '#shared/types/TestimonialCardType';
import ArticleHero from '~/components/page-building/article/ArticleHero.vue';
import ArticleView from '~/components/page-building/article/ArticleView.vue';
import { formattedDate } from '~/utils/helpers';

const props = defineProps<{
    story: any;
}>();

const article = computed<any>(() => {
    return props.story.content;
});

const articleData = computed<ArticleType>(() => {
    const sections = article.value.content_sections.map((section) => {
        if (section.component === 'article_content_section') {
            return {
                id: section.heading,
                heading: section.heading,
                subheading: section.subheading,
                content: richTextToHtml(section.content) as string,
                type: 'article_content_section',
                vEditable: section,
            } as ArticleContentSectionType;
        }
        if (section.component === 'customer_testimonial') {
            return {
                id: section.name,
                name: section.name,
                businessName: section.business_name,
                testimonial: section.testimonial,
                type: 'customer_testimonial',
                vEditable: section,
            } as TestimonialCardType;
        }
    });

    return {
        showHero: article.value.show_hero,
        image: {
            title: article.value.image.title,
            alt: article.value.image.alt,
            src: article.value.image.filename,
        } as ImageType,
        categories: article.value.categories,
        date: formattedDate(article.value.date),
        title: article.value.title,
        subtitle: article.value.subtitle,
        shortDescription: richTextToHtml(
            article.value.short_description,
        ) as string,
        description: richTextToHtml(article.value.description) as string,
        url: article.value.slug,
        sections,
        showCta: article.value.show_cta,
        ctaHeading: article.value.cta_heading,
        ctaText: article.value.cta_text,
        ctaLink: {
            label: article.value.cta_link_label,
            url: '/',
            vEditable: article.value.cta_link,
        } as LinkType,
    } as ArticleType;
});

const heroData = computed<ArticleHeroType>(() => {
    return {
        image: {
            title: article.value.image.title,
            alt: article.value.image.alt,
            src: article.value.image.filename,
        } as ImageType,
        categories: article.value.categories,
        date: formattedDate(article.value.date),
        title: article.value.title,
        shortDescription: richTextToHtml(
            article.value.short_description,
        ) as string,
        description: richTextToHtml(article.value.description) as string,
        backgroundColor: article.value.background_color
            ? article.value.background_color
            : 'white',
        buttons: [],
        nextBackgroundColor: 'white',
        bottomAngle: article.value.background_angle
            ? article.value.background_angle
            : 'straight',
    };
});
</script>
